import { useEffect, useLayoutEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react'
import classNames from 'classnames'
import { createUseStyles } from 'react-jss'
import usePrevious from '@/hooks/usePrevious'
import SplitText from '@/vendors/bonus-files-for-npm-users/umd/SplitText'
import animations from './animations'
import style from './style'

const useStyles = createUseStyles(style)

const SimpleSplitText = forwardRef(({
  animation,
  appear,
  className,
  delay,
  disableHover,
  duration,
  text,
  text2,
}, ref) => {
  const classes = useStyles()
  const $root = useRef()
  const $split1 = useRef(null)
  const $split2 = useRef(null)
  const $firstSplit = useRef(null)
  const $secondSplit = useRef(null)
  const $firstText = useRef()
  const $secondText = useRef()
  const timelineHover = useRef()
  const timelineAppear = useRef()
  const [animationReady, setAnimationReady] = useState(false)
  const [complete, setComplete] = useState(false)

  const prepareAnimation = () => {
    if (text !== undefined && text !== '') {
      $split1.current = new SplitText($firstText.current, { type: 'chars' })
      $split2.current = new SplitText($secondText.current, { type: 'chars' })
    }
    if ($split1.current !== null && $split2.current !== null) {
      if (appear) {
        if (animation === 'split') animations.setSplit({ $chars: $split1.current.chars })
        if (animation === 'linear') animations.setLinear({ $chars: $split1.current.chars })
      }
      if (animation === 'split') animations.setSplit({ $chars: $split2.current.chars })
      if (animation === 'linear') animations.setSplit({ $chars: $split2.current.chars })
      $firstSplit.current = $split1.current.chars
      $secondSplit.current = $split2.current.chars
      setAnimationReady(true)
    }
  }

  const completeAnimation = () => setComplete(true)

  useLayoutEffect(() => {
    if (animationReady) {
      if (animation === 'split') {
        // Hover Timeline Animation
        timelineHover.current = animations.splitHover({
          $first: $firstSplit.current,
          $second: $secondSplit.current,
          delay,
        })
        // Appear Timeline Animation
        timelineAppear.current = animations.splitAppear({
          $first: $firstSplit.current,
          delay,
          duration,
          complete: completeAnimation(),
        })
      }
      if (animation === 'linear') {
        // Hover Timeline Animation
        timelineHover.current = animations.linearHover({
          $first: $firstSplit.current,
          $second: $secondSplit.current,
          delay,
        })
        // Appear Timeline Animation
        timelineAppear.current = animations.linearAppear({
          $first: $firstSplit.current,
          delay,
          duration,
          complete: completeAnimation(),
        })
      }
    }
  }, [animationReady])

  const animateIn = () => timelineHover.current?.play()

  const animateOut = () => timelineHover.current?.reverse()

  const animateAppear = () => timelineAppear.current?.play()

  const handleMouseEnter = () => animateIn()

  const handleMouseLeave = () => animateOut()

  const prevText = usePrevious(text)
  useEffect(() => {
    if (prevText !== text) setAnimationReady(false)
    if (!animationReady) prepareAnimation()
  }, [text, animationReady])

  useImperativeHandle(ref, () => ({
    appear: animateAppear,
    animateIn,
    animateOut,
    ref: $root.current,
  }))

  return (
    <span
      className={classNames({
        [classes.root]: true,
        [className]: className,
        [classes.animationNotReady]: !animationReady,
      })}
      onMouseEnter={!disableHover ? handleMouseEnter : () => {}}
      onMouseLeave={!disableHover ? handleMouseLeave : () => {}}
      ref={$root}
      data-text={text2?.length > text?.length ? text2 : text}
    >
      <div
        className={classNames({
          [classes.firstText]: true,
          SimpleSplitText__firstText: true,
          [classes.complete]: complete,
        })}
        ref={$firstText}
        dangerouslySetInnerHTML={{ __html: text }}
      />
      <div
        className={classNames({
          [classes.secondText]: true,
          SimpleSplitText__secondText: true,
          [classes.complete]: complete,
        })}
        ref={$secondText}
        dangerouslySetInnerHTML={{ __html: text2 || text }}
      />
    </span>
  )
})

SimpleSplitText.defaultProps = {
  delay: 0,
  disableHover: false,
  appear: false,
  animation: 'split',
  duration: 1,
}

export default SimpleSplitText
